import { useState, useRef, useEffect } from 'react'
import { Search, MapPin, X } from 'lucide-react'
import type { UKLocation } from '../data/ukLocations'

interface LocationSearchProps {
  onSelectLocation: (lat: number, lng: number) => void
  onSearchPostcode: (postcode: string) => void
  isLoading?: boolean
}

const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i

let locationsCache: UKLocation[] | null = null
async function getLocations(): Promise<UKLocation[]> {
  if (locationsCache) return locationsCache
  const mod = await import('../data/ukLocations')
  locationsCache = mod.UK_LOCATIONS
  return locationsCache
}

export function LocationSearch({
  onSelectLocation,
  onSearchPostcode,
  isLoading = false
}: LocationSearchProps) {
  const [value, setValue] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [filteredLocations, setFilteredLocations] = useState<UKLocation[]>([])
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!value.trim()) {
      setFilteredLocations([])
      setShowSuggestions(false)
      return
    }

    const search = value.toLowerCase().trim()

    if (/^\d/.test(search) || UK_POSTCODE_REGEX.test(value.trim())) {
      setFilteredLocations([])
      setShowSuggestions(false)
      return
    }

    let cancelled = false
    getLocations().then((locations) => {
      if (cancelled) return
      const startsWith: UKLocation[] = []
      const contains: UKLocation[] = []
      for (const loc of locations) {
        const lower = loc.name.toLowerCase()
        if (lower.startsWith(search)) startsWith.push(loc)
        else if (lower.includes(search)) contains.push(loc)
        if (startsWith.length >= 10) break
      }
      const filtered = [...startsWith, ...contains].slice(0, 10)
      setFilteredLocations(filtered)
      setShowSuggestions(filtered.length > 0)
    })
    return () => { cancelled = true }
  }, [value])

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSubmit = async () => {
    const trimmed = value.trim()
    if (!trimmed) return

    if (UK_POSTCODE_REGEX.test(trimmed)) {
      onSearchPostcode(trimmed)
      setShowSuggestions(false)
      return
    }

    const locations = await getLocations()
    const location = locations.find(
      (loc) => loc.name.toLowerCase() === trimmed.toLowerCase()
    )
    if (location) {
      onSelectLocation(location.lat, location.lng)
      setValue(location.name)
      setShowSuggestions(false)
    } else if (filteredLocations.length > 0) {
      onSelectLocation(filteredLocations[0].lat, filteredLocations[0].lng)
      setValue(filteredLocations[0].name)
      setShowSuggestions(false)
    }
  }

  const handleLocationSelect = (location: UKLocation) => {
    setValue(location.name)
    onSelectLocation(location.lat, location.lng)
    setShowSuggestions(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  return (
    <div className="relative flex-1" ref={containerRef}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => value.trim() && filteredLocations.length > 0 && setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            placeholder="Town, village or postcode"
            disabled={isLoading}
            className="w-full pl-9 pr-8 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:opacity-50"
          />
          <MapPin
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          {value && (
            <button
              type="button"
              onClick={() => {
                setValue('')
                setShowSuggestions(false)
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-0.5"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <button
          onClick={handleSubmit}
          disabled={isLoading || !value.trim()}
          className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center"
          aria-label="Search"
        >
          <Search size={18} />
        </button>
      </div>

      {/* Autocomplete suggestions */}
      {showSuggestions && filteredLocations.length > 0 && (
        <div className="absolute top-full left-0 right-12 mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 max-h-60 overflow-y-auto z-50">
          {filteredLocations.map((location) => (
            <button
              key={`${location.name}-${location.lat}`}
              onClick={() => handleLocationSelect(location)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-between"
            >
              <span className="text-slate-900 dark:text-white font-medium">
                {location.name}
              </span>
              <span className="text-slate-400 dark:text-slate-500 text-xs shrink-0">
                {location.type} · {location.region}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
